import React, { useState, useEffect, useCallback } from 'react';
import { Upload, X, Image as ImageIcon, Film, Trash2, Check, Loader2, Search, Copy } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatBytes(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

// ---- Full Media Manager (for the admin Media tab) ----
export const MediaManager = ({ token }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [uploadError, setUploadError] = useState('');

  const headers = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchFiles = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/admin/media`, { headers: headers() });
    if (res.ok) setFiles(await res.json());
  }, [headers]);

  useEffect(() => { if (token) fetchFiles(); }, [token, fetchFiles]);

  const handleUpload = async (e) => {
    const uploadFiles = Array.from(e.target.files);
    if (!uploadFiles.length) return;
    
    // Validate file sizes before upload
    const oversizedFiles = uploadFiles.filter(f => f.size > MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      setUploadError(`File(s) too large (max 10MB): ${oversizedFiles.map(f => `${f.name} (${formatBytes(f.size)})`).join(', ')}`);
      e.target.value = '';
      return;
    }
    
    setUploadError('');
    setUploading(true);
    for (let i = 0; i < uploadFiles.length; i++) {
      const file = uploadFiles[i];
      setProgress(`Uploading ${i + 1}/${uploadFiles.length}: ${file.name} (${formatBytes(file.size)})`);
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await fetch(`${API_URL}/api/admin/media/upload`, { method: 'POST', headers: headers(), body: formData });
        if (!res.ok) {
          const err = await res.json();
          setUploadError(`Failed to upload ${file.name}: ${err.detail || 'Unknown error'}`);
        }
      } catch (err) {
        console.error('Upload failed:', file.name, err);
        setUploadError(`Upload failed: ${file.name}`);
      }
    }
    setUploading(false);
    setProgress('');
    fetchFiles();
    e.target.value = '';
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this file?')) return;
    await fetch(`${API_URL}/api/admin/media/${id}`, { method: 'DELETE', headers: headers() });
    fetchFiles();
  };

  const copyUrl = (url) => {
    const fullUrl = `${API_URL}${url}`;
    navigator.clipboard.writeText(fullUrl);
    alert(`URL copied!\n\n${fullUrl}\n\nPaste this URL in any image/media field.`);
  };

  const filtered = files.filter(f => {
    if (filter === 'image' && f.media_type !== 'image') return false;
    if (filter === 'video' && f.media_type !== 'video') return false;
    if (search && !f.original_filename.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-6" data-testid="media-manager">
      {/* Instructions */}
      <div className="bg-s-primary/10 border border-s-primary/30 rounded-lg p-4">
        <h4 className="text-s-primary font-semibold text-sm mb-2">📸 How to Use Media</h4>
        <ol className="text-xs text-s-text2 space-y-1 list-decimal list-inside">
          <li><strong>Upload:</strong> Click below or drag files (JPG, PNG, WebP, MP4, WebM - max 10MB)</li>
          <li><strong>Copy URL:</strong> Hover over any image and click the copy icon</li>
          <li><strong>Use URL:</strong> Paste the copied URL in any image field (Hero, Packages, etc.)</li>
        </ol>
        <p className="text-xs text-s-muted mt-2">💡 Tip: Uploaded images are stored permanently in the database. External URLs (unsplash, etc.) also work.</p>
      </div>

      {/* Upload error message */}
      {uploadError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-center gap-3">
          <X className="w-5 h-5 text-red-400 flex-shrink-0" />
          <p className="text-red-400 text-sm">{uploadError}</p>
          <button onClick={() => setUploadError('')} className="ml-auto text-red-400 hover:text-red-300">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Upload area */}
      <div className="bg-s-bg2 border-2 border-dashed border-s-border rounded-xl p-8 text-center hover:border-s-primary/50 transition-colors">
        <input type="file" id="media-upload" className="hidden" multiple accept="image/*,video/mp4,video/webm" onChange={handleUpload} data-testid="media-upload-input" />
        <label htmlFor="media-upload" className="cursor-pointer flex flex-col items-center gap-3">
          {uploading ? (
            <>
              <Loader2 className="w-10 h-10 text-s-primary animate-spin" />
              <p className="text-s-text text-sm">{progress}</p>
            </>
          ) : (
            <>
              <Upload className="w-10 h-10 text-s-muted" />
              <p className="text-s-text font-medium">Drop files here or click to upload</p>
              <p className="text-s-muted text-xs">Images (JPG, PNG, WebP) & Videos (MP4, WebM) — Max 10MB each</p>
            </>
          )}
        </label>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-s-muted" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search files..." className="pl-9 bg-s-bg2 border-s-border" data-testid="media-search" />
        </div>
        <div className="flex gap-1">
          {['all', 'image', 'video'].map(f => (
            <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter === f ? 'bg-s-primary text-s-text' : 'bg-s-bg2 text-s-muted hover:text-s-text'}`} data-testid={`media-filter-${f}`}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        <span className="text-s-muted text-xs ml-auto">{filtered.length} file{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Gallery */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3" data-testid="media-gallery">
        {filtered.map(file => (
          <MediaCard key={file.id} file={file} onDelete={handleDelete} onCopy={copyUrl} />
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-16 text-s-muted">
            {files.length === 0 ? 'No files uploaded yet' : 'No files match your search'}
          </div>
        )}
      </div>
    </div>
  );
};

const MediaCard = ({ file, onDelete, onCopy, onSelect, selected }) => (
  <div
    className={`group relative bg-s-bg2 border rounded-lg overflow-hidden transition-all ${selected ? 'border-s-primary ring-2 ring-s-primary/30' : 'border-s-border hover:border-s-primary/40'} ${onSelect ? 'cursor-pointer' : ''}`}
    onClick={onSelect ? () => onSelect(file) : undefined}
    data-testid={`media-item-${file.id}`}
  >
    <div className="aspect-[4/3] bg-s-card flex items-center justify-center overflow-hidden">
      {file.media_type === 'image' ? (
        <img src={`${API_URL}${file.url}`} alt={file.original_filename} className="w-full h-full object-contain bg-gray-100" loading="lazy" />
      ) : (
        <div className="flex flex-col items-center gap-2 text-s-muted">
          <Film className="w-8 h-8" />
          <span className="text-[10px]">Video</span>
        </div>
      )}
    </div>
    <div className="p-2">
      <p className="text-s-text text-[11px] truncate" title={file.original_filename}>{file.original_filename}</p>
      <p className="text-s-muted text-[10px]">{formatBytes(file.size)}</p>
    </div>
    {/* Actions overlay */}
    <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
      {onCopy && (
        <button onClick={(e) => { e.stopPropagation(); onCopy(file.url); }} className="p-1 bg-black/70 rounded text-white hover:bg-s-primary" data-testid={`copy-url-${file.id}`}>
          <Copy className="w-3 h-3" />
        </button>
      )}
      {onDelete && (
        <button onClick={(e) => { e.stopPropagation(); onDelete(file.id); }} className="p-1 bg-black/70 rounded text-red-400 hover:bg-red-500 hover:text-white" data-testid={`delete-media-${file.id}`}>
          <Trash2 className="w-3 h-3" />
        </button>
      )}
    </div>
    {selected && (
      <div className="absolute top-1 left-1 p-1 bg-s-primary rounded-full">
        <Check className="w-3 h-3 text-white" />
      </div>
    )}
  </div>
);

// ---- Image Picker Modal (for use in forms) ----
export const ImagePickerButton = ({ value, onChange, token, label = "Browse Media" }) => {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');

  const headers = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchFiles = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/admin/media`, { headers: headers() });
    if (res.ok) setFiles(await res.json());
  }, [headers]);

  useEffect(() => { if (open && token) fetchFiles(); }, [open, token, fetchFiles]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API_URL}/api/admin/media/upload`, { method: 'POST', headers: headers(), body: formData });
      if (res.ok) {
        const data = await res.json();
        onChange(`${API_URL}${data.url}`);
        setOpen(false);
      }
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSelect = (file) => {
    onChange(`${API_URL}${file.url}`);
    setOpen(false);
  };

  const filtered = files.filter(f =>
    f.media_type === 'image' &&
    (!search || f.original_filename.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <>
      <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)} className="border-s-border text-s-muted hover:text-s-text h-8 text-xs" data-testid="image-picker-btn">
        <ImageIcon className="w-3 h-3 mr-1" />
        {label}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-s-bg2 border-s-border text-s-text max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Select Image</DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-s-muted" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." className="pl-9 bg-s-bg border-s-border" />
            </div>
            <input type="file" id="picker-upload" className="hidden" accept="image/*" onChange={handleUpload} />
            <label htmlFor="picker-upload" className="cursor-pointer">
              <Button type="button" variant="outline" size="sm" className="border-s-primary text-s-primary" asChild>
                <span>{uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1" />Upload New</>}</span>
              </Button>
            </label>
          </div>
          <div className="grid grid-cols-4 gap-2 max-h-[50vh] overflow-y-auto">
            {filtered.map(file => (
              <MediaCard key={file.id} file={file} onSelect={handleSelect} selected={value === `${API_URL}${file.url}`} />
            ))}
            {filtered.length === 0 && <p className="col-span-4 text-center py-8 text-s-muted text-sm">No images found</p>}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
